import type { ModelMessage } from "ai";
import {
	getAgentChatSession,
	setAgentChatSession,
} from "./agent-chat-session-store";
import { appNavigationSkill } from "./skills/app-navigation";
import { boardDrawingSkill } from "./skills/board-drawing";
import { boardEditingSkill } from "./skills/board-editing";
import { boardGenerationSkill } from "./skills/board-generation";
import { cardWiringSkill } from "./skills/card-wiring";
import { imageGenerationSkill } from "./skills/image-generation";
import { libraryLifecycleSkill } from "./skills/library-lifecycle";
import type {
	AgentSkill,
	AgentSkillAvailability,
	AgentSkillSurface,
} from "./skills/types";

export type {
	AgentSkill,
	AgentSkillAvailability,
	AgentSkillSurface,
} from "./skills/types";

export const AGENT_SKILL_BODY_MAX_CHARS = 8000;

const SKILL_TRUNCATION_NOTE = `\n[Skill body truncated to ${AGENT_SKILL_BODY_MAX_CHARS} characters.]`;

const BUILTIN_SKILLS: AgentSkill[] = [
	imageGenerationSkill,
	appNavigationSkill,
	libraryLifecycleSkill,
	boardGenerationSkill,
	boardEditingSkill,
	boardDrawingSkill,
	cardWiringSkill,
];

export function capSkillBody(body: string) {
	if (body.endsWith(SKILL_TRUNCATION_NOTE)) {
		return body;
	}

	if (body.length <= AGENT_SKILL_BODY_MAX_CHARS) {
		return body;
	}

	return `${body.slice(0, AGENT_SKILL_BODY_MAX_CHARS)}${SKILL_TRUNCATION_NOTE}`;
}

export function listAvailableSkills(
	availability: AgentSkillAvailability = {},
): AgentSkill[] {
	return BUILTIN_SKILLS.filter((skill) =>
		isSkillSurfaceAvailable(skill.surface, availability),
	);
}

export function getAgentSkill(
	name: string,
	availability: AgentSkillAvailability = {},
): AgentSkill | undefined {
	return listAvailableSkills(availability).find((skill) => skill.name === name);
}

export function listAgentSkillNames(
	availability: AgentSkillAvailability = {},
): string[] {
	return listAvailableSkills(availability).map((skill) => skill.name);
}

export function buildSkillCatalogLines(
	availability: AgentSkillAvailability = {},
): string[] {
	return listAvailableSkills(availability).map(
		(skill) => `- ${skill.name}: ${skill.description}`,
	);
}

export function rememberLoadedAgentSkill(name: string) {
	const current = getAgentChatSession().loadedSkillNames;
	if (current.includes(name)) {
		return;
	}

	setAgentChatSession({
		loadedSkillNames: [...current, name],
	});
}

function isSkillSurfaceAvailable(
	surface: AgentSkillSurface,
	availability: AgentSkillAvailability,
) {
	if (surface === "always") {
		return true;
	}

	if (surface === "app") {
		return availability.hasAppTools === true;
	}

	if (surface === "library") {
		return availability.hasLibraryTools === true;
	}

	if (surface === "board") {
		return availability.hasBoardTools === true;
	}

	return false;
}

function collectStrings(value: unknown, out: string[]) {
	if (typeof value === "string") {
		out.push(value);
		return;
	}

	if (Array.isArray(value)) {
		for (const item of value) {
			collectStrings(item, out);
		}
		return;
	}

	if (value && typeof value === "object") {
		for (const nested of Object.values(value)) {
			collectStrings(nested, out);
		}
	}
}

function modelMessagesContainSkillBody(
	messages: ModelMessage[],
	body: string,
): boolean {
	const strings: string[] = [];
	collectStrings(messages, strings);
	return strings.some((text) => text.includes(body));
}

/**
 * Re-attach skill bodies that fell out of the trimmed history window.
 * They go on the instructions string. This gateway rejects system messages
 * inside the messages array.
 */
export function withLoadedSkillInstructions(
	system: string,
	messages: ModelMessage[],
	loadedSkillNames: readonly string[],
	availability: AgentSkillAvailability = {},
): string {
	const extras: string[] = [];

	for (const name of loadedSkillNames) {
		const skill = getAgentSkill(name, availability);
		if (!skill) {
			continue;
		}

		const body = capSkillBody(skill.body);
		if (modelMessagesContainSkillBody(messages, body)) {
			continue;
		}

		extras.push(
			[
				`Previously loaded skill ${skill.name}.`,
				"Re-attached because it is no longer in recent history.",
				body,
			].join("\n"),
		);
	}

	if (extras.length === 0) {
		return system;
	}

	return [system, ...extras].join("\n\n");
}
